import type { Emotion } from '../types/emotion';
import type { PlayPose } from './heroPlay';

/**
 * Footer chase — a small background moment while Pixel waits in the footer.
 *
 * After Pixel has sat in the footer for ~10s, two tiny bots race through far behind him, left to right
 * and out the right side: a freaked-out one in front, an angry one hot on its tail. Pixel hums along,
 * oblivious… does a double take, follows them across with his eyes, stares after them for a beat, then
 * shrugs it off and turns back to you. No collisions, no loops — just the chase.
 *
 * Pure function of time: visitor positions are normalised screen units at a fixed far depth.
 */

export const CHASE_DEPTH = -9;
const START_AFTER = 10;
const REARM_AFTER = 40;
export const CHASE_DUR = 8.6;

/** Seconds a bot takes to cross the screen */
const CROSS = 4.4;
const RUNNER_AT = 0.3;
const CHASER_AT = 1.0;
const START_U = -1.3;
const END_U = 1.3;

/** Pixel's beats */
const NOTICE = 1.7;
const LINGER = 5.9;
const TURN_BACK = 7.0;

export type VisitorMood = 'scared' | 'angry';

export interface Visitor {
  /** -1..1 of the half-width / half-height at the visitor's depth */
  u: number;
  v: number;
  /** Extra depth relative to CHASE_DEPTH (positive = closer) */
  dz: number;
  /** Body turn: 0 = facing the camera, 1 ≈ side-on, moving right */
  yaw: number;
  pitch: number;
  roll: number;
  squash: number;
  /** 0..1 visibility */
  pop: number;
  /** Their own face */
  mood: VisitorMood;
  /** Where their eyes point (face space, -1..1) */
  look: { x: number; y: number };
}

export interface ChaseFrame {
  pose: PlayPose;
  visitors: [Visitor, Visitor];
  /** Which visitor Pixel watches (index), or null when he's looking elsewhere */
  watch: number | null;
  /** 0..1 how strongly his eyes + head follow the watched visitor */
  watchK: number;
}

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
const smooth = (v: number) => {
  const c = clamp01(v);
  return c * c * (3 - 2 * c);
};
const pulse = (v: number, at: number, len: number) => (v > at && v < at + len ? Math.sin(((v - at) / len) * Math.PI) : 0);

function base(rate = 4): PlayPose {
  return {
    mode: 'none',
    x: 0, y: 0, z: 0, pitch: 0, yaw: 0, roll: 0, squint: 0, led: 0, board: 0,
    wide: 0, spiral: 0, tight: 0, scan: 0,
    heading: 0, spin: 0, arms: null, emotion: null, gaze: null, headFollow: 0.5, rate, push: false,
  };
}

/** Straight-across progress 0..1 with a quick start (no slow-down: they're in a hurry) */
const crossing = (s: number) => {
  const k = clamp01(s / CROSS);
  return k < 0.12 ? (k * k) / 0.24 : k - 0.06;
};

export class FooterChase {
  private sinceIn = -1;
  private startedAt = -1;
  private nextAllowed = 0;

  reset() {
    this.sinceIn = -1;
    this.startedAt = -1;
  }

  /** `enabled`: Pixel is settled in the footer and free to act */
  update(t: number, enabled: boolean): ChaseFrame | null {
    if (!enabled) {
      this.reset();
      return null;
    }
    if (this.sinceIn < 0) this.sinceIn = t;
    if (this.startedAt < 0) {
      if (t - this.sinceIn < START_AFTER || t < this.nextAllowed) return null;
      this.startedAt = t;
    }
    const a = t - this.startedAt;
    if (a >= CHASE_DUR) {
      this.startedAt = -1;
      this.nextAllowed = t + REARM_AFTER;
      return null;
    }
    return { pose: this.pixel(a), visitors: [this.runner(a), this.chaser(a)], ...this.watching(a) };
  }

  // ── The two bots ───────────────────────────────────────────────────────────────────────────────

  private runner(a: number): Visitor {
    const s = a - RUNNER_AT;
    const k = crossing(s);
    // Panicky: jittery zig-zag, keeps glancing back over its shoulder
    const glance = pulse(s, 1.1, 0.5) + pulse(s, 2.6, 0.5);
    return {
      u: START_U + (END_U - START_U) * k,
      v: 0.2 + Math.sin(s * 3.1) * 0.05 + Math.sin(s * 17) * 0.012,
      dz: 0,
      yaw: 0.5 - glance * 0.75,
      pitch: -0.12,
      roll: Math.sin(s * 3.1) * -0.18 + Math.sin(s * 23) * 0.05,
      squash: 0,
      pop: s > 0 && s < CROSS ? 1 : 0,
      mood: 'scared',
      look: { x: -0.6 * glance + Math.sin(s * 19) * 0.12, y: 0.1 + Math.sin(s * 13) * 0.08 },
    };
  }

  private chaser(a: number): Visitor {
    const s = a - CHASER_AT;
    const k = crossing(s);
    // Determined: steadier line, leaning hard into the chase, eyes locked ahead
    return {
      u: START_U + (END_U - START_U) * k,
      v: 0.17 + Math.sin(s * 2) * 0.03,
      dz: 0,
      yaw: 0.55,
      pitch: -0.28,
      roll: Math.sin(s * 2) * -0.08,
      squash: 0,
      pop: s > 0 && s < CROSS ? 1 : 0,
      mood: 'angry',
      look: { x: 0.55, y: 0 },
    };
  }

  // ── Pixel ──────────────────────────────────────────────────────────────────────────────────────

  private watching(a: number): { watch: number | null; watchK: number } {
    if (a < NOTICE) return { watch: null, watchK: 0 };
    // Double take: quick glance, back to you, then he locks on
    const glance = pulse(a, NOTICE, 0.35);
    const lock = smooth((a - (NOTICE + 0.6)) / 0.25) * (1 - smooth((a - TURN_BACK) / 0.6));
    // Follows the runner until it's gone, then the angry one
    const runnerGone = a > RUNNER_AT + CROSS * 0.97;
    return { watch: runnerGone ? 1 : 0, watchK: Math.max(glance, lock) };
  }

  private pixel(a: number): PlayPose {
    const p = base(5);
    let emotion: Emotion = 'happy';

    if (a < NOTICE) {
      // Oblivious: humming to himself, gentle sway, eyes on you
      const bpm = 96 / 60;
      const beat = Math.sin(a * bpm * Math.PI);
      p.x = beat * 0.05;
      p.roll = beat * 0.07;
      p.pitch = Math.exp(-((a * bpm) % 1) * 8) * 0.05;
      p.squint = 0.3;
      p.gaze = { x: 0, y: 0 };
      p.headFollow = 0.2;
    } else if (a < LINGER) {
      // "…huh?" — double take, then rapt attention as they race past
      emotion = a < NOTICE + 0.9 ? 'surprised' : 'curious';
      p.z = pulse(a, NOTICE + 0.55, 0.4) * 0.16;
      p.wide = smooth((a - NOTICE - 0.6) / 0.4) * 0.7;
      p.roll = Math.sin(a * 1.4) * 0.04;
      p.headFollow = 0.95;
    } else if (a < TURN_BACK) {
      // Stares after them at the right edge… head tilt: "what was that?"
      emotion = 'curious';
      p.roll = smooth((a - LINGER) / 0.4) * 0.2;
      p.wide = 0.4;
      p.headFollow = 0.9;
    } else {
      // Shrugs it off and turns back to you
      emotion = 'happy';
      p.gaze = { x: 0, y: 0 };
      p.headFollow = 0.3;
      const shrug = pulse(a, TURN_BACK + 0.2, 0.5);
      p.y = shrug * 0.06;
      p.roll = shrug * -0.08;
      p.squint = 0.3;
    }
    p.emotion = emotion;

    const env = smooth(a / 0.6) * smooth((CHASE_DUR - a) / 0.8);
    for (const k of ['x', 'y', 'z', 'pitch', 'yaw', 'roll', 'squint', 'led', 'wide'] as const) p[k] *= env;
    return p;
  }
}

export const footerChase = new FooterChase();
